import {
  BookOpen,
  Calculator,
  Database,
  Download,
  Info,
  Search,
  Table2,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useCostAccountingCalculation } from '../hooks/useCostAccounting.js';
import { downloadCsv, escapeCell } from '../lib/csv.js';
import { CostAccountingDefinitionView } from '../components/cost-accounting/CostAccountingDefinitionView.js';
import { CostAccountingTrendTable } from '../components/cost-accounting/CostAccountingTrendTable.js';
import { CostAccountingItemDetail } from '../components/cost-accounting/CostAccountingItemDetail.js';
import { CostAccountingSummaryView } from '../components/cost-accounting/CostAccountingSummaryView.js';
import { CostAccountingVerificationDetail } from '../components/cost-accounting/CostAccountingVerificationDetail.js';
import { CostAccountingDataValidation } from '../components/cost-accounting/CostAccountingDataValidation.js';
import type {
  CostAccountingItem,
  MonthlyTrendEntry,
  RawItemForExport,
} from '../types/cost-accounting.js';

/** @graph-connects none */
type ViewMode = 'single' | 'trend' | 'verification-detail' | 'data-validation' | 'definition';

/** @graph-connects none */
function currentMonthJst(): string {
  const jst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return `${String(jst.getUTCFullYear())}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** @graph-connects none */
const EVENT_LABELS: Record<string, string> = { sold: '販売', disposed: '廃棄' };

/** @graph-connects none */
function buildSummaryCsv(trend: MonthlyTrendEntry[]): string {
  const BOM = '﻿';
  const header = '年月,減価償却費,廃棄原価,販売原価,償却残,当月計上合計';
  const rows = trend.map((t) =>
    [
      escapeCell(t.month),
      String(t.depreciation),
      String(t.disposal),
      String(t.sale),
      String(t.remaining),
      String(t.total),
    ].join(','),
  );
  return `${BOM}${header}\r\n${rows.join('\r\n')}`;
}

/** @graph-connects none */
function buildRawDataCsv(rawItems: RawItemForExport[]): string {
  const BOM = '﻿';
  const header =
    '個品ID,SKU ID,ステータスコード,ステータス名,償却対象,入荷日,廃棄日,取得原価(税抜下代),上代(税抜),仕入明細あり,更新日';
  const rows = rawItems.map((r) =>
    [
      String(r.itemId),
      escapeCell(r.itemCode),
      String(r.itemStatus),
      escapeCell(r.statusName),
      r.isDepreciating ? 'Y' : 'N',
      escapeCell(r.arrivalDate),
      r.disposedDate ? escapeCell(r.disposedDate) : '',
      r.acquisitionCost !== null ? String(r.acquisitionCost) : '',
      r.retailPrice !== null ? String(r.retailPrice) : '',
      r.hasProcurement ? 'Y' : 'N',
      r.updatedAt ? escapeCell(r.updatedAt) : '',
    ].join(','),
  );
  return `${BOM}${header}\r\n${rows.join('\r\n')}`;
}

/** @graph-connects none */
function buildItemsCsv(items: CostAccountingItem[]): string {
  const BOM = '﻿';
  const header =
    '個品ID,アイテムコード,取得原価,累計償却額,当月償却額,簿価,入荷日,ステータス,イベント種別,イベント日,計上額';
  const rows = items.map((item) =>
    [
      String(item.itemId),
      escapeCell(item.itemCode),
      String(item.acquisitionCost),
      String(item.cumulativeDepreciation),
      String(item.monthlyDepreciation),
      String(item.bookValue),
      escapeCell(item.arrivalDate),
      escapeCell(item.status),
      item.eventType ? escapeCell(EVENT_LABELS[item.eventType] ?? item.eventType) : '',
      item.eventDate ? escapeCell(item.eventDate) : '',
      item.eventCost !== null ? String(item.eventCost) : '',
    ].join(','),
  );
  return `${BOM}${header}\r\n${rows.join('\r\n')}`;
}

/** labelKey は描画側で t() する翻訳キー。 @graph-connects none */
const VIEW_TABS: { mode: ViewMode; icon: typeof Table2; labelKey: string }[] = [
  { mode: 'single', icon: Table2, labelKey: 'costAccounting.tabSingle' },
  { mode: 'trend', icon: TrendingUp, labelKey: 'costAccounting.tabTrend' },
  { mode: 'verification-detail', icon: Search, labelKey: 'costAccounting.tabVerificationDetail' },
  { mode: 'data-validation', icon: Database, labelKey: 'costAccounting.tabDataValidation' },
  { mode: 'definition', icon: BookOpen, labelKey: 'costAccounting.tabDefinition' },
];

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business Cost accounting page for mens items. Computes 24-month straight-line depreciation, disposal/sale costs on demand. Displays summary cards, verification panel, monthly trend, item details, definitions, verification detail with drill-down, and data validation with sync status. Supports CSV download for summary, item details, and pre-calculation raw data.
 * @graph-connects useCostAccountingCalculation [calls] Cost accounting mutation hook
 * @graph-connects downloadCsv [calls] CSV download utility
 * @graph-connects CostAccountingSummaryView [calls] Summary cards and verification
 * @graph-connects CostAccountingTrendTable [calls] Monthly trend table
 * @graph-connects CostAccountingItemDetail [calls] Item detail table
 * @graph-connects CostAccountingDefinitionView [calls] Metric definitions
 * @graph-connects CostAccountingVerificationDetail [calls] Verification detail with drill-down
 * @graph-connects CostAccountingDataValidation [calls] Data validation with sync status
 */
export function CostAccountingPage() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(currentMonthJst());
  const [viewMode, setViewMode] = useState<ViewMode>('single');
  const mutation = useCostAccountingCalculation();
  const data = mutation.data;

  const handleCalculate = (): void => {
    mutation.mutate(month);
  };

  const handleSummaryCsvDownload = (): void => {
    if (!data) {
      return;
    }
    downloadCsv(`cost_accounting_summary_${data.month}.csv`, buildSummaryCsv(data.monthlyTrend));
  };

  const handleItemsCsvDownload = (): void => {
    if (!data) {
      return;
    }
    downloadCsv(`cost_accounting_items_${data.month}.csv`, buildItemsCsv(data.items));
  };

  const handleRawDataCsvDownload = (): void => {
    if (!data) {
      return;
    }
    downloadCsv(`cost_accounting_raw_${data.month}.csv`, buildRawDataCsv(data.rawItems));
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        Finance
      </div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
        {t('costAccounting.title')}
      </h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        {t('costAccounting.description')}
      </p>

      <Card className="mt-6 flex flex-wrap items-end gap-4 p-4">
        <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
          {t('costAccounting.targetMonth')}
          <input
            type="month"
            value={month}
            max={currentMonthJst()}
            onChange={(e) => {
              setMonth(e.target.value);
            }}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
          />
        </label>
        <Button variant="primary" onClick={handleCalculate} disabled={mutation.isPending}>
          <Calculator className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {mutation.isPending ? t('costAccounting.calculating') : t('costAccounting.calculate')}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-lg border border-[var(--color-border)]">
            {VIEW_TABS.map((tab, i) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.mode}
                  type="button"
                  onClick={() => {
                    setViewMode(tab.mode);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 text-xs transition-colors ${i === 0 ? 'rounded-l-lg' : ''} ${i === VIEW_TABS.length - 1 ? 'rounded-r-lg' : ''} ${i > 0 ? 'border-l border-[var(--color-border)]' : ''} ${viewMode === tab.mode ? 'bg-[var(--color-accent-primary)] text-white' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'}`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {viewMode === 'definition' ? (
        <CostAccountingDefinitionView />
      ) : mutation.isPending ? (
        <p className="mt-8 text-sm text-[var(--color-text-muted)]">
          {t('costAccounting.calculating')}
        </p>
      ) : mutation.isError ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('costAccounting.calculateError')}
          </p>
        </Card>
      ) : !data ? (
        <Card className="mt-6 p-8 text-center">
          <Calculator
            className="mx-auto h-8 w-8 text-[var(--color-text-muted)]"
            aria-hidden="true"
          />
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
            {t('costAccounting.promptCalculate')}
          </p>
        </Card>
      ) : viewMode === 'single' ? (
        <CostAccountingSummaryView
          summary={data.summary}
          verification={data.verification}
          delta={data.delta}
          onSummaryCsvDownload={handleSummaryCsvDownload}
          onViewVerificationDetail={() => {
            setViewMode('verification-detail');
          }}
        />
      ) : viewMode === 'verification-detail' ? (
        <CostAccountingVerificationDetail
          verification={data.verification}
          delta={data.delta}
          items={data.items}
          unknownCostItems={data.unknownCostItems}
          targetMonth={data.month}
        />
      ) : viewMode === 'data-validation' ? (
        <CostAccountingDataValidation pipelineSteps={data.pipelineSteps} month={data.month} />
      ) : viewMode === 'trend' ? (
        <>
          <CostAccountingTrendTable trend={data.monthlyTrend} />
          <div className="mt-3 flex justify-end">
            <Button variant="secondary" onClick={handleSummaryCsvDownload}>
              <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {t('costAccounting.summaryCsv')}
            </Button>
          </div>
        </>
      ) : null}

      {(viewMode === 'single' || viewMode === 'trend') && data && (
        <>
          <CostAccountingItemDetail
            items={data.items}
            unknownCount={data.unknownCostItems.length}
            onCsvDownload={handleItemsCsvDownload}
          />

          {data.unknownCostItems.length > 0 && (
            <div className="mt-6 rounded-xl border border-dashed border-[var(--color-border)] bg-[#faf5ff] p-4 dark:bg-[#1f162e]">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#7c3aed]">
                <Info className="h-4 w-4" aria-hidden="true" />
                {t('costAccounting.unknownCostItems', {
                  num: String(data.unknownCostItems.length),
                })}
              </div>
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                {t('costAccounting.unknownCostItemsNote')}
              </p>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={handleRawDataCsvDownload}>
              <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {t('costAccounting.rawDataCsv')}
            </Button>
          </div>

          <div className="mt-6 flex gap-6 text-xs text-[var(--color-text-muted)]">
            <span>
              {t('costAccounting.calculatedAt', {
                value: data.calculatedAt.replace('T', ' ').slice(0, 19),
              })}
            </span>
            <span>
              {t('costAccounting.targetItemsSummary', {
                total: (data.items.length + data.unknownCostItems.length).toLocaleString('ja-JP'),
                calculated: data.items.length.toLocaleString('ja-JP'),
              })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
